import { queryOptions, useQuery } from '@tanstack/react-query';

import { NodeLabelsFilter, NodeLabelsFilterLabelValue } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';
import { TimeRange } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

const ORGANIZATION_NODE_TEMPLATES_REPORT_QUERY_KEY =
  'organization-node-templates-report';

const getOrganizationNodeTemplatesReportQueryKey = ({
  organizationId,
  key,
  nodeLabels,
  timeZone,
  operator,
  step,
  clusterIds,
}: Partial<UseOrganizationNodeTemplatesReportQueryArgs> = {}) =>
  [
    ORGANIZATION_NODE_TEMPLATES_REPORT_QUERY_KEY,
    ...(organizationId !== undefined
      ? [organizationId, key, nodeLabels, timeZone, operator, step, clusterIds]
      : []),
  ] as const;

type NodeLabelSelection = {
  key: string;
  values: string[];
};

type UseOrganizationNodeTemplatesReportQueryArgs = {
  organizationId: string;
  key: string;
  getRange: () => TimeRange;
  nodeLabels?: NodeLabelSelection[];
  timeZone: string;
  operator?: 'AND' | 'OR';
  enabled?: boolean;
  step?: 'ONE_DAY' | 'ONE_MONTH';
  clusterIds?: string[];
};

const buildNodeLabelsFilter = (
  nodeLabels: NodeLabelSelection[],
  operator?: 'AND' | 'OR'
): NodeLabelsFilter => ({
  labels: nodeLabels.map<NodeLabelsFilterLabelValue>(
    (label): NodeLabelsFilterLabelValue => ({
      key: label.key,
      operator: 'IN',
      values: label.values,
    })
  ),
  operator: operator ?? 'OR',
});

const getOrganizationNodeTemplatesReportQueryOptions = ({
  organizationId,
  key,
  getRange,
  timeZone,
  nodeLabels = [],
  step = 'ONE_DAY',
  clusterIds = [],
  operator = 'OR',
  enabled = true,
}: UseOrganizationNodeTemplatesReportQueryArgs) => {
  const { from: startTime, to: endTime } = getRange();

  return queryOptions({
    queryKey: getOrganizationNodeTemplatesReportQueryKey({
      organizationId,
      key,
      nodeLabels,
      operator,
      clusterIds,
      timeZone,
      step,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getNodeTemplatesMetrics({
        organizationId,
        nodeLabelsFilter: buildNodeLabelsFilter(nodeLabels, operator),
        clusterIds,
        startTime,
        endTime,
        step,
      });
      return data;
    },
    enabled: makeIsEnabled(!!organizationId, enabled),
  });
};

export const useOrganizationNodeTemplatesReportQuery = (
  props: UseOrganizationNodeTemplatesReportQueryArgs
) => {
  return useQuery(getOrganizationNodeTemplatesReportQueryOptions(props));
};
